import { FC, useMemo } from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import useRoiServicePerformance from 'hooks/useRoiServicePerformance';
import { formatUsdCurrency } from 'utils/formatUsdCurrency';
import MetricCell from '../MetricCell/MetricCell';

interface ServicePerformanceProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const PAGE_SIZE = 10;

const ServicePerformance: FC<ServicePerformanceProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const { data, isLoading, isValidating } = useRoiServicePerformance({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const services = useMemo(
    () => data?.periodData?.servicePerformance ?? [],
    [data]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Service',
        accessor: (row: any) => row.name,
        id: 'name',
      },
      {
        Header: 'Appts Requested',
        accessor: (row: any) => row.totalRequestAppointments,
        id: 'totalRequestAppointments',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(
              ...data.map((d: any) => d.totalRequestAppointments),
              0
            )}
          />
        ),
      },
      // {
      //   Header: 'Shows',
      //   accessor: (row: any) => row.totalCompletedAppointments,
      //   id: 'totalCompletedAppointments',
      //   isMetric: true,
      //   Cell: ({ value, data }: any) => (
      //     <MetricCell
      //       value={value}
      //       max={Math.max(
      //         ...data.map((d: any) => d.totalCompletedAppointments),
      //         0
      //       )}
      //     />
      //   ),
      // },
      {
        Header: 'New Patients',
        accessor: (row: any) => row.totalNewPatients,
        id: 'totalNewPatients',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalNewPatients), 0)}
          />
        ),
      },
      {
        Header: 'Existing Patients',
        accessor: (row: any) => row.totalExistingPatients,
        id: 'totalExistingPatients',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalExistingPatients), 0)}
          />
        ),
      },

      {
        Header: 'Production',
        accessor: (row: any) => row.totalProduction,
        id: 'totalProduction',
        isMetric: true,
        Cell: ({ value, data }: any) => (
          <MetricCell
            value={value}
            max={Math.max(...data.map((d: any) => d.totalProduction), 0)}
            format={(v) => formatUsdCurrency(v)}
          />
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: services,
      initialState: { pageSize: PAGE_SIZE },
      disableSortRemove: true,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white">
      <span className="text-16 font-bold leading-[2.4rem] block mb-1.6">
        Service Performance
      </span>

      <div className="overflow-x-auto">
        <table {...getTableProps()} className="w-full text-14 leading-[2.1rem]">
          <thead className="font-bold">
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className="border-b border-light-grey"
              >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="text-left p-0 py-1.9 px-1.6"
                  >
                    <div
                      className={`flex items-center gap-x-0.9 cursor-pointer ${
                        (column as any).isMetric ? 'justify-end' : ''
                      }`}
                    >
                      <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                        {column.render('Header')}
                      </span>
                      <SortArrow
                        className={`${
                          column.isSorted ? 'fill-magenta' : 'fill-[#9CA3AF]'
                        } ${
                          column.isSorted && !column.isSortedDesc
                            ? 'rotate-180'
                            : ''
                        } transition-all`}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {isLoading || isValidating ? (
            <tbody>
              <tr>
                <td colSpan={6}>
                  <div className="w-full h-[24rem] flex justify-center items-center">
                    <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : services.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={6}>
                  <div className="w-full h-[24rem] flex flex-col gap-y-1.6 justify-center items-center">
                    <EmptyBucket />
                    <span>No service available on this list</span>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()} className="text-darkest-grey">
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    className="bg-white border-b border-light-grey last:border-0 group"
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        className="text-left p-0 py-1.9 px-1.6"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>

      {!isLoading && services.length > 0 && (
        <Pagination
          isBorderNotIncluded
          data={{
            currPage: pageIndex + 1,
            perPage: pageSize,
            total: services.length,
          }}
          handlePaginating={(paginate: ITablePaginate) => {
            gotoPage(paginate.currPage - 1);
            setPageSize(paginate.perPage);
          }}
        />
      )}
    </div>
  );
};

export default ServicePerformance;
